import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import { fatLossReportService, generateFatLossReportHTML, prefillFatLossFormData } from '../../services/fatLossReportService';
import { patientService } from '../../services/patientService';
import { format } from 'date-fns';
import { requestPDF } from '../../services/pdfService';
import PublishingOverlay from '../common/PublishingOverlay';
import { generatePDFFilename } from '../../utils/generatePDFFilename';

const FatLossReportForm = () => {
  const navigate = useNavigate();
  const { id, patientId } = useParams();
  const appointmentId = patientId;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [publishingStatus, setPublishingStatus] = useState(null);
  const [patient, setPatient] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [readOnlyFields, setReadOnlyFields] = useState(new Set());

  const [formData, setFormData] = useState({
    patient_id: '',
    patient_name: '',
    patient_gender: '',
    patient_age: '',
    exam_date: format(new Date(), 'yyyy-MM-dd'),
    
    // Fat loss assessment fields
    body_fat_percentage: '',
    almi: '',
    visceral_fat_area: '',
    ast_alt_ratio: '',
    triglycerides: '',
    fasting_insulin: '',
    homa_ir: '',
    cortisol: '',
    tsh: '',
    free_t3: '',
    hscrp: '',
    resting_metabolic_rate: '',
    target_calories: '',
    zone2_heart_rate: '',
    blockers_summary: '',
    recommendations: '',
    
    pdf_url: ''
  });

  const sections = [
    {
      label: 'Patient Information',
      fields: [
        { name: 'patient_name', label: 'Patient Name', type: 'text', required: true },
        { name: 'patient_age', label: 'Age', type: 'number', required: true },
        { name: 'patient_gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'], required: true },
        { name: 'exam_date', label: 'Exam Date', type: 'date', required: true }
      ]
    },
    {
      label: 'Fat Loss Assessment',
      component: 'fatloss_assessment'
    }
  ];

  // Helper function to calculate age from DOB
  const calculateAgeFromDOB = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to normalize gender
  const normalizeGender = (gender) => {
    if (!gender) return '';
    const genderLower = gender.toLowerCase().trim();
    if (genderLower === 'male' || genderLower === 'm') return 'Male';
    if (genderLower === 'female' || genderLower === 'f') return 'Female';
    if (genderLower === 'other') return 'Other';
    return gender;
  };

  // Load patient data
  useEffect(() => {
    const loadPatientData = async () => {
      try {
        if (appointmentId) {
          console.log('Loading patient data for appointmentId:', appointmentId);
          const response = await patientService.getPatientById(appointmentId);
          console.log('Patient data response:', response);
          
          const patientData = response.data?.data || response.data || response;
          console.log('Extracted patient data:', patientData);
          
          setPatient(patientData);
          
          // Extract and process patient information
          const profileData = patientData.profile || {};
          const patientName = profileData.name || '';
          const patientGender = normalizeGender(profileData.gender);
          
          let patientPhone = patientData.user.phone;
          let patientAge = profileData.age;
          if (!patientAge && profileData.dob) {
            patientAge = calculateAgeFromDOB(profileData.dob);
          }
          
          setFormData(prev => ({
            ...prev,
            patient_id: patientData.id || '',
            patient_name: patientName,
            patient_gender: patientGender,
            patient_age: patientAge || '',
            patient_phone: patientPhone || ''
          }));
          
          // Fetch existing fat loss report data by appointment ID
          try {
            console.log('Attempting to fetch existing fat loss report for appointmentId:', appointmentId);
            const reportData = await fatLossReportService.getReportByAppointmentId(appointmentId);
            console.log('Report data response:', reportData);
            
            if (reportData && reportData.data) {
              setReportId(reportData.data.id);
              setIsEditMode(true);
              console.log('Existing report found, setting edit mode with reportId:', reportData.data.id);
              
              // Transform API response to form data structure
              const apiData = reportData.data;
              const transformedData = {
                patient_id: apiData.patient_id || '',
                patient_name: apiData.patient_name || '',
                patient_gender: apiData.patient_gender || '',
                patient_age: apiData.patient_age || '',
                exam_date: apiData.exam_date || '',
                patient_phone: patientPhone || '',
                // Convert string values from API to float for form display
                body_fat_percentage: apiData.body_fat_percentage ? parseFloat(apiData.body_fat_percentage) : '',
                almi: apiData.almi ? parseFloat(apiData.almi) : '',
                visceral_fat_area: apiData.visceral_fat_area ? parseFloat(apiData.visceral_fat_area) : '',
                ast_alt_ratio: apiData.ast_alt_ratio ? parseFloat(apiData.ast_alt_ratio) : '',
                triglycerides: apiData.triglycerides ? parseFloat(apiData.triglycerides) : '',
                fasting_insulin: apiData.fasting_insulin ? parseFloat(apiData.fasting_insulin) : '',
                homa_ir: apiData.homa_ir ? parseFloat(apiData.homa_ir) : '',
                cortisol: apiData.cortisol ? parseFloat(apiData.cortisol) : '',
                tsh: apiData.tsh ? parseFloat(apiData.tsh) : '',
                free_t3: apiData.free_t3 ? parseFloat(apiData.free_t3) : '',
                hscrp: apiData.hscrp ? parseFloat(apiData.hscrp) : '',
                resting_metabolic_rate: apiData.resting_metabolic_rate ? parseFloat(apiData.resting_metabolic_rate) : '',
                target_calories: apiData.target_calories ? parseFloat(apiData.target_calories) : '',
                zone2_heart_rate: apiData.zone2_heart_rate ? String(apiData.zone2_heart_rate) : '',
                blockers_summary: apiData.blockers_summary || '',
                recommendations: apiData.recommendations || '',
                
                pdf_url: apiData.pdf_url || ''
              };
              
              setFormData(prev => ({
                ...prev,
                ...transformedData
              }));
            } else {
              console.log('No report data found in response');
              setIsEditMode(false);
            }
          } catch (reportError) {
            console.log('No existing fat loss report found or error fetching:', reportError.message);
            setIsEditMode(false);
            
            // Prefill form data from DEXA and VO2Max reports if no existing fat loss report
            try {
              console.log('Attempting to prefill form data from DEXA and VO2Max reports...');
              const prefillData = await prefillFatLossFormData(appointmentId);
              console.log('Prefill data received:', prefillData);
              
              if (prefillData) {
                setFormData(prev => ({
                  ...prev,
                  // Update patient info if available
                  patient_name: prefillData.patientInfo?.patientName || prev.patient_name,
                  patient_gender: prefillData.patientInfo?.gender || prev.patient_gender,
                  patient_age: prefillData.patientInfo?.age || prev.patient_age,
                  exam_date: prefillData.patientInfo?.appointmentDate || prev.exam_date,
                  // Update biomarker data if available
                  body_fat_percentage: prefillData.fatLossAssessment?.bodyFatPercentage || prev.body_fat_percentage,
                  visceral_fat_area: prefillData.fatLossAssessment?.visceralFatArea || prev.visceral_fat_area,
                  almi: prefillData.fatLossAssessment?.almi || prev.almi,
                  zone2_heart_rate: prefillData.fatLossAssessment?.zone2HeartRate || prev.zone2_heart_rate,
                  resting_metabolic_rate: prefillData.fatLossAssessment?.restingMetabolicRate || prev.resting_metabolic_rate,
                  triglycerides: prefillData.fatLossAssessment?.triglycerides || prev.triglycerides,
                  cortisol: prefillData.fatLossAssessment?.cortisol || prev.cortisol,
                  tsh: prefillData.fatLossAssessment?.tsh || prev.tsh,
                  free_t3: prefillData.fatLossAssessment?.freeT3 || prev.free_t3,
                  hscrp: prefillData.fatLossAssessment?.hscrp || prev.hscrp,
                  homa_ir: prefillData.fatLossAssessment?.homaIr || prev.homa_ir
                }));
                
                // Set read-only fields from prefill data
                setReadOnlyFields(prefillData.readOnlyFields);
                
                const dataSource = prefillData.readOnlyFields.size > 0 ? 'published reports' : 'Fat Loss API';
                toast.info(`Form prefilled with data from ${dataSource}`);
              }
            } catch (prefillError) {
              console.warn('Error prefilling form data:', prefillError.message);
              // Don't show error toast as this is optional
            }
          }
        } else {
          console.log('No appointmentId provided');
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load patient data');
      } finally {
        setLoading(false);
      }
    };

    loadPatientData();
  }, [appointmentId]);

  const renderPatientInfoSection = () => {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Patient Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.patient_name || ''}
              onChange={(e) => handleFormChange('patient_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter patient name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Age <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.patient_age || ''}
              onChange={(e) => handleFormChange('patient_age', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter age"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.patient_gender || ''}
              onChange={(e) => handleFormChange('patient_gender', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Exam Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.exam_date || ''}
              onChange={(e) => handleFormChange('exam_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
      </div>
    );
  };

  // Helper function to check if a field is read-only
  const isFieldReadOnly = (fieldName) => {
    return readOnlyFields.has(fieldName);
  };

  // Helper function to get CSS classes for read-only fields
  const getFieldClassName = (fieldName) => {
    const baseClass = 'w-full px-3 py-2 border rounded text-sm';
    if (isFieldReadOnly(fieldName)) {
      return `${baseClass} border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed`;
    }
    return `${baseClass} border-gray-300`;
  };

  // Helper function to handle float input
  const handleFloatChange = (fieldName, value) => {
    // Don't allow changes to read-only fields
    if (isFieldReadOnly(fieldName)) {
      return;
    }
    // Allow empty string or valid float numbers
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      handleFormChange(fieldName, value ? parseFloat(value) : '');
    }
  };

  const renderFatLossAssessmentSection = () => {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Body Fat Percentage {isFieldReadOnly('body_fat_percentage') && <span className="text-blue-500 text-xs">(from DEXA)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.body_fat_percentage || ''}
              onChange={(e) => handleFloatChange('body_fat_percentage', e.target.value)}
              disabled={isFieldReadOnly('body_fat_percentage')}
              className={getFieldClassName('body_fat_percentage')}
              placeholder="Enter body fat percentage"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              ALMI (Appendicular Lean Mass Index) {isFieldReadOnly('almi') && <span className="text-blue-500 text-xs">(from DEXA)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.almi || ''}
              onChange={(e) => handleFloatChange('almi', e.target.value)}
              disabled={isFieldReadOnly('almi')}
              className={getFieldClassName('almi')}
              placeholder="Enter ALMI value"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Visceral Fat Area {isFieldReadOnly('visceral_fat_area') && <span className="text-blue-500 text-xs">(from DEXA)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.visceral_fat_area || ''}
              onChange={(e) => handleFloatChange('visceral_fat_area', e.target.value)}
              disabled={isFieldReadOnly('visceral_fat_area')}
              className={getFieldClassName('visceral_fat_area')}
              placeholder="Enter visceral fat area"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              AST/ALT Ratio
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.ast_alt_ratio || ''}
              onChange={(e) => handleFloatChange('ast_alt_ratio', e.target.value)}
              className={getFieldClassName('ast_alt_ratio')}
              placeholder="Enter AST/ALT ratio"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Triglycerides {isFieldReadOnly('triglycerides') && <span className="text-blue-500 text-xs">(from Blood)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.triglycerides || ''}
              onChange={(e) => handleFloatChange('triglycerides', e.target.value)}
              disabled={isFieldReadOnly('triglycerides')}
              className={getFieldClassName('triglycerides')}
              placeholder="Enter triglycerides value"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Fasting Insulin
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.fasting_insulin || ''}
              onChange={(e) => handleFloatChange('fasting_insulin', e.target.value)}
              className={getFieldClassName('fasting_insulin')}
              placeholder="Enter fasting insulin"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              HOMA-IR {isFieldReadOnly('homa_ir') && <span className="text-blue-500 text-xs">(from Blood)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.homa_ir || ''}
              onChange={(e) => handleFloatChange('homa_ir', e.target.value)}
              disabled={isFieldReadOnly('homa_ir')}
              className={getFieldClassName('homa_ir')}
              placeholder="Enter HOMA-IR value"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Cortisol {isFieldReadOnly('cortisol') && <span className="text-blue-500 text-xs">(from Blood)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.cortisol || ''}
              onChange={(e) => handleFloatChange('cortisol', e.target.value)}
              disabled={isFieldReadOnly('cortisol')}
              className={getFieldClassName('cortisol')}
              placeholder="Enter cortisol value"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              TSH {isFieldReadOnly('tsh') && <span className="text-blue-500 text-xs">(from Blood)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.tsh || ''}
              onChange={(e) => handleFloatChange('tsh', e.target.value)}
              disabled={isFieldReadOnly('tsh')}
              className={getFieldClassName('tsh')}
              placeholder="Enter TSH value"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Free T3 {isFieldReadOnly('free_t3') && <span className="text-blue-500 text-xs">(from Blood)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.free_t3 || ''}
              onChange={(e) => handleFloatChange('free_t3', e.target.value)}
              disabled={isFieldReadOnly('free_t3')}
              className={getFieldClassName('free_t3')}
              placeholder="Enter Free T3 value"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              hsCRP {isFieldReadOnly('hscrp') && <span className="text-blue-500 text-xs">(from Blood)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.hscrp || ''}
              onChange={(e) => handleFloatChange('hscrp', e.target.value)}
              disabled={isFieldReadOnly('hscrp')}
              className={getFieldClassName('hscrp')}
              placeholder="Enter hsCRP value"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Resting Metabolic Rate {isFieldReadOnly('resting_metabolic_rate') && <span className="text-blue-500 text-xs">(from VO2Max)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.resting_metabolic_rate || ''}
              onChange={(e) => handleFloatChange('resting_metabolic_rate', e.target.value)}
              disabled={isFieldReadOnly('resting_metabolic_rate')}
              className={getFieldClassName('resting_metabolic_rate')}
              placeholder="Enter RMR value"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Target Calories <span className="text-blue-500 text-xs">(Auto-calculated)</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.target_calories || ''}
              disabled={true}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
              placeholder="Calculated from RMR"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Zone 2 Heart Rate {isFieldReadOnly('zone2_heart_rate') && <span className="text-blue-500 text-xs">(from VO2Max)</span>}
            </label>
            <input
              type="text"
              value={formData.zone2_heart_rate || ''}
              onChange={(e) => handleFormChange('zone2_heart_rate', e.target.value)}
              disabled={isFieldReadOnly('zone2_heart_rate')}
              className={getFieldClassName('zone2_heart_rate')}
              placeholder="e.g., 141-156"
            />
          </div>
        </div>
      </div>
    );
  };

  const handlePreview = async () => {
    try {
      const html = await generateFatLossReportHTML(formData);
      sessionStorage.setItem('fatLossReportData', JSON.stringify(formData));
      
      const previewUrl = `/fatloss-report/${appointmentId}/preview`;
      window.open(previewUrl, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    }
  };

  const handleSubmit = async (status) => {
    setSubmitting(true);
    let pdf_url = null;
    
    try {
      if (status === 'published') {
        setPublishingStatus('generating');
        try {
          const html = await generateFatLossReportHTML(formData);
          const pdfBlob = await requestPDF(html);
          const pdfObjectUrl = URL.createObjectURL(pdfBlob);
          
          const patientName = patient?.profile?.name || formData.patient_name || patient?.name || 'patient';
          const orderId = patient?.sukra_order_id || patient?.id || 'unknown';
          const fileName = generatePDFFilename(patientName, 'fatloss', orderId);
          console.log('Uploading PDF with filename:', fileName);
          
          try {
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            const uploadFormData = new FormData();
            uploadFormData.append('file', pdfFile);
            uploadFormData.append('file_name', fileName);
            
            const token = localStorage.getItem('token');
            
            const uploadRes = await fetch('https://vitaldev.vitalinsights.in/user/upload-file-to-s3', {
              method: 'POST',
              body: uploadFormData,
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (!uploadRes.ok) {
              const errorText = await uploadRes.text();
              console.error('Upload error response:', errorText);
              throw new Error(`Upload failed with status ${uploadRes.status}: ${errorText}`);
            }
            
            const response = await uploadRes.json();
            const fileUrl = response.fileUrl || response.file_url || response.url || response.data?.fileUrl || response.data?.file_url;
            
            if (!fileUrl) {
              console.error('No file URL found in response. Full response:', response);
              throw new Error('Invalid response from server - no file URL');
            }
            
            pdf_url = String(fileUrl);
            console.log('Successfully set PDF URL:', pdf_url);
          } catch (uploadError) {
            console.error('Error during PDF upload:', uploadError);
            pdf_url = pdfObjectUrl;
            console.warn('Using object URL as fallback:', pdf_url);
          }
          
          console.log('PDF generated and uploaded:', pdf_url);
        } catch (error) {
          console.error('Error generating PDF:', error);
          setPublishingStatus('error');
          toast.error('Failed to generate PDF');
          setSubmitting(false);
          return;
        }
      }
      
      // Convert float values to strings for API submission
      const payload = {
        ...formData,
        // Convert float biomarker values to strings
        body_fat_percentage: formData.body_fat_percentage !== '' ? String(formData.body_fat_percentage) : '',
        almi: formData.almi !== '' ? String(formData.almi) : '',
        visceral_fat_area: formData.visceral_fat_area !== '' ? String(formData.visceral_fat_area) : '',
        ast_alt_ratio: formData.ast_alt_ratio !== '' ? String(formData.ast_alt_ratio) : '',
        triglycerides: formData.triglycerides !== '' ? String(formData.triglycerides) : '',
        fasting_insulin: formData.fasting_insulin !== '' ? String(formData.fasting_insulin) : '',
        homa_ir: formData.homa_ir !== '' ? String(formData.homa_ir) : '',
        cortisol: formData.cortisol !== '' ? String(formData.cortisol) : '',
        tsh: formData.tsh !== '' ? String(formData.tsh) : '',
        free_t3: formData.free_t3 !== '' ? String(formData.free_t3) : '',
        hscrp: formData.hscrp !== '' ? String(formData.hscrp) : '',
        resting_metabolic_rate: formData.resting_metabolic_rate !== '' ? String(formData.resting_metabolic_rate) : '',
        target_calories: formData.target_calories !== '' ? String(formData.target_calories) : '',
        zone2_heart_rate: formData.zone2_heart_rate !== '' ? String(formData.zone2_heart_rate) : '',
        report_status: status,
        appointment_id: appointmentId
      };
      
      if (pdf_url) {
        console.log('Setting PDF URL in submission:', pdf_url);
        payload.pdf_url = pdf_url;
      }

      console.log('About to submit with payload:', JSON.stringify(payload));
      
      const response = await fatLossReportService.createReport(payload);
      console.log('Create report response:', response);
      toast.success('Report submitted successfully');

      if (status === 'published') {
        setPublishingStatus('success');
        setTimeout(() => {
          setPublishingStatus(null);
          setSubmitting(false);
          navigate('/patients');
        }, 2000);
      } else {
        setSubmitting(false);
        navigate('/patients');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit report');
      setSubmitting(false);
      if (status === 'published') {
        setPublishingStatus('error');
      }
    }
  };

  const handleCancel = () => {
    navigate('/patients');
  };

  const goToNextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goToPreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderField = (field) => {
    const value = formData[field.name];

    if (field.type === 'textarea') {
      return (
        <div key={field.name} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={value || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="w-full p-2 border rounded"
            rows="4"
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.name} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={value || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.name} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
        <input
          type={field.type}
          value={value || ''}
          onChange={(e) => handleFormChange(field.name, e.target.value)}
          className="w-full p-2 border rounded"
          placeholder={`Enter ${field.label.toLowerCase()}`}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {publishingStatus && (
        <PublishingOverlay status={publishingStatus} />
      )}
      
      <div className="flex flex-col md:flex-row gap-4 p-4">
        <div className="md:w-64 flex-shrink-0">
          <div className="bg-white p-4 rounded-lg shadow space-y-6">
            {/* Patient Information */}
            {patient && (
              <div className="border-b pb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-3">{patient.profile?.name}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID:</span>
                    <span className="font-medium">{patient.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Branch:</span>
                    <span className="font-medium">{patient.sukra_branch_id}</span>
                  </div>
                  {patient.slot_start_time && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Appointment:</span>
                      <span className="font-medium">{patient.slot_start_time?.split("T")[0]}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Report Sections */}
            <div>
              <h3 className="text-xl font-bold text-orange-500 mb-4">Report Sections</h3>
              <ul className="space-y-2">
                {sections.map((section, index) => (
                  <li key={index}>
                    <button
                      onClick={() => setCurrentSection(index)}
                      disabled={submitting}
                      className={`w-full text-left py-2 px-3 rounded transition ${currentSection === index 
                        ? 'bg-orange-500 text-white font-medium' 
                        : 'hover:bg-gray-100'}`}
                    >
                      {section.label}
                      {currentSection === index && (
                        <span className="float-right">→</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        <div className="flex-1">
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold">
                {isEditMode ? 'Edit Fat Loss Report' : 'New Fat Loss Report'}
              </h1>
            </div>
            
            <div className="mb-4">
              <h2 className="text-xl font-semibold">
                {sections[currentSection].label}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Complete all fields in this section before proceeding
              </p>
            </div>

            <div className="space-y-6">
              {sections[currentSection].component === 'fatloss_assessment' ? (
                renderFatLossAssessmentSection()
              ) : (
                sections[currentSection].fields.map(field => renderField(field))
              )}
            </div>

            <div className="flex justify-between mt-6">
              <Button
                variant="secondary"
                onClick={currentSection > 0 ? goToPreviousSection : handleCancel}
              >
                {currentSection > 0 ? 'Previous' : 'Back to List'}
              </Button>

              {currentSection === sections.length - 1 ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handlePreview}
                    disabled={submitting}
                  >
                    Preview Report
                  </Button>
                  <Button
                    onClick={() => handleSubmit('submitted')}
                    disabled={submitting}
                    variant="secondary"
                  >
                    {submitting ? 'Saving...' : 'Submit Report'}
                  </Button>
                  <Button
                    onClick={() => handleSubmit('published')}
                    disabled={submitting}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {submitting ? 'Publishing...' : 'Submit & Publish'}
                  </Button>
                </div>
              ) : (
                <Button onClick={goToNextSection}>Next</Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default FatLossReportForm;
